// biome-ignore-all lint/suspicious/noArrayIndexKey: no options
import { Box } from '@mantine/core';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import type { FC } from 'react';
import { useHostContext } from '@/context/HostContext.tsx';
import { cn } from '@/utils';
import {
	COLOR_POINTS,
	OPACITY_POINTS,
	PHRASE_EASE,
	SCALE_POINTS,
	type TextCarouselLoaderTrackPoint
} from './TextCarouselLoader.constants';

interface TextCarouselLoaderProps {
	className?: string;
	phrases: React.ReactNode[];
	phraseDuration?: number;
}

interface TextCarouselLoaderTrack {
	keyframes: (number | string)[];
	transition: {
		duration: number;
		ease: typeof PHRASE_EASE;
		repeat: number;
		times: number[];
	};
}

const buildTrack = (
	points: readonly TextCarouselLoaderTrackPoint[],
	startTime: number,
	cycleDuration: number
): TextCarouselLoaderTrack => {
	const [, restValue] = points[0];
	const windowEnd = startTime + Math.max(...points.map(([seconds]) => seconds));
	const bounded: TextCarouselLoaderTrackPoint[] = [
		...(startTime > 0 ? [[0, restValue] as TextCarouselLoaderTrackPoint] : []),
		...points.map(
			([seconds, value]) =>
				[startTime + seconds, value] as TextCarouselLoaderTrackPoint
		),
		...(windowEnd < cycleDuration
			? [[cycleDuration, restValue] as TextCarouselLoaderTrackPoint]
			: [])
	];

	return {
		keyframes: bounded.map(([, value]) => value),
		transition: {
			duration: cycleDuration,
			ease: PHRASE_EASE,
			repeat: Number.POSITIVE_INFINITY,
			times: bounded.map(([seconds]) => seconds / cycleDuration)
		}
	};
};

const TextCarouselLoader: FC<TextCarouselLoaderProps> = ({
	className,
	phrases,
	phraseDuration = 5
}) => {
	const { isMiniCreatorMode, isOverlayMode, isPageMode, localization } =
		useHostContext();
	const cycleDuration = Math.max(phrases.length, 1) * phraseDuration;

	return (
		<Box
			className={cn(
				'relative w-full h-full flex items-center justify-center',
				className,
				{
					'sm:min-w-[360px] md:min-w-[400px] xl:min-w-[480px]': isPageMode
				}
			)}
		>
			{phrases.map((phrase, index) => {
				const startTime = index * phraseDuration;
				const opacity = buildTrack(OPACITY_POINTS, startTime, cycleDuration);
				const color = buildTrack(COLOR_POINTS, startTime, cycleDuration);
				const scale = buildTrack(SCALE_POINTS, startTime, cycleDuration);

				return (
					<motion.div
						animate={{
							color: color.keyframes as string[],
							opacity: opacity.keyframes as number[],
							scale: scale.keyframes as number[]
						}}
						className={cn(
							'absolute inset-0 flex items-center justify-center text-center',
							'[font-family:var(--font-headlines)] text-primary-900 ',
							'font-medium text-[#888]',
							{
								'text-3xl': isPageMode,
								'text-xl': isOverlayMode,
								'text-sm': isMiniCreatorMode
							}
						)}
						key={index}
						transition={{
							color: color.transition,
							opacity: opacity.transition,
							scale: scale.transition
						}}
					>
						<span className="flex items-center gap-2">
							<span>
								<ArrowRight
									className={cn({
										'size-6': isPageMode,
										'size-5': isOverlayMode,
										'size-4': isMiniCreatorMode
									})}
								/>
							</span>
							{localization.translate(
								'creator',
								`scrappingWaitingScreenList.${phrase}`
							)}
						</span>
					</motion.div>
				);
			})}
		</Box>
	);
};

export default TextCarouselLoader;
